import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { GithubTokenTerms } from './GithubTokenTerms';
import { buttonClass } from './ui/Button';

// Connect form for the user-token lane (#21). Presenter — owns only the two
// ephemeral inputs (the PAT + the repos textarea); the container owns the POST
// and its busy/error. The constraint text sits ABOVE the fields, always shown.
export type GithubTokenConnectFormProps = {
  readonly busy: boolean;
  /** Inline reason from a failed connect (`ApiError.detail`). */
  readonly error: string | null;
  readonly onConnect: (input: { token: string; repos: string }) => void;
};

export function GithubTokenConnectForm(props: GithubTokenConnectFormProps): JSX.Element {
  const [pat, setPat] = createSignal('');
  const [repos, setRepos] = createSignal('');
  const valid = (): boolean => pat().trim().length > 0 && repos().trim().length > 0;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.busy) return;
    props.onConnect({ token: pat().trim(), repos: repos() });
  };

  return (
    <form class="github-token__form" onSubmit={submit}>
      <GithubTokenTerms />
      <Field
        label={t('github_token.pat_label')}
        id="github-token-pat"
        desc={t('github_token.pat_desc')}
      >
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="password"
            autocomplete="off"
            value={pat()}
            onInput={(e) => setPat(e.currentTarget.value)}
            placeholder={t('github_token.pat_placeholder')}
            required
          />
        )}
      </Field>
      <Field
        label={t('github_token.repos_input_label')}
        id="github-token-repos"
        desc={t('github_token.repos_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            rows="3"
            value={repos()}
            onInput={(e) => setRepos(e.currentTarget.value)}
            placeholder={t('github_token.repos_placeholder')}
            required
          />
        )}
      </Field>
      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>
      <div class="github-token__form-actions">
        <button type="submit" class={buttonClass()} disabled={!valid() || props.busy}>
          {t(props.busy ? 'github_token.connecting' : 'github_token.connect')}
        </button>
      </div>
    </form>
  );
}
